/**
 * Packaging OS — Visual Direction page (Phase 27A).
 * POST /api/workflows/visual-direction/dry-run.
 */

import { useState, type FormEvent } from "react";
import {
  DRY_RUN_CHANNELS,
  DRY_RUN_LANGUAGES,
  INTAKE_OBJECTIVES,
  PRICE_BANDS,
  runVisualDirectionDryRun,
  type ApiResult,
  type DryRunChannel,
  type DryRunLanguage,
  type IntakeObjective,
  type PriceBand,
  type VisualDirectionRequest,
  type VisualDirectionResponse,
  runWorkflowPersistedFirst,
} from "../api/client";
import { AIAssistToggle } from "../components/AIAssistToggle";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import {
  FieldGrid,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  product_name: string;
  category: string;
  brand_name: string;
  brand_personality: string;
  target_market: string;
  target_customer: string;
  packaging_objective: IntakeObjective;
  price_band: PriceBand;
  sales_channel: string;
  visual_preference: string;
  colors_to_use: string;
  colors_to_avoid: string;
  typography_preference: string;
  competitor_references: string;
  packaging_format: string;
  packaging_material: string;
  claims_to_avoid: string;
  target_language: DryRunLanguage;
  notes: string;
}

const EMPTY: FormState = {
  product_name: "",
  category: "",
  brand_name: "",
  brand_personality: "",
  target_market: "",
  target_customer: "",
  packaging_objective: "new_packaging",
  price_band: "unknown",
  sales_channel: "",
  visual_preference: "",
  colors_to_use: "",
  colors_to_avoid: "",
  typography_preference: "",
  competitor_references: "",
  packaging_format: "",
  packaging_material: "",
  claims_to_avoid: "",
  target_language: "en",
  notes: "",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<VisualDirectionResponse> };

interface FieldErrors {
  product_name?: string;
  category?: string;
  brand_name?: string;
  target_market?: string;
}

function splitLines(s: string, max: number): string[] {
  return s
    .split("\n")
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
    .slice(0, max);
}

function splitCsv(s: string, max: number): string[] {
  return s
    .split(/[,\n]/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
    .slice(0, max);
}

export function VisualDirectionPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    target_language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
      ? locale
      : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [aiAssist, setAiAssist] = useState<boolean>(false);

  function update<K extends keyof FormState>(k: K, v: FormState[K]): void {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function validate(): FieldErrors {
    const e: FieldErrors = {};
    const r = t.workflowDryRunRequiredHint;
    if (!form.product_name.trim()) e.product_name = r;
    if (!form.category.trim()) e.category = r;
    if (!form.brand_name.trim()) e.brand_name = r;
    if (!form.target_market.trim()) e.target_market = r;
    return e;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const body: VisualDirectionRequest = {
      product_name: form.product_name.trim(),
      category: form.category.trim(),
      brand_name: form.brand_name.trim(),
      brand_personality: form.brand_personality.trim() || null,
      target_market: form.target_market.trim().toUpperCase(),
      target_customer: form.target_customer.trim() || null,
      packaging_objective: form.packaging_objective,
      price_band: form.price_band,
      sales_channel: form.sales_channel
        ? (form.sales_channel as DryRunChannel)
        : null,
      visual_preference: form.visual_preference.trim() || null,
      colors_to_use: splitCsv(form.colors_to_use, 8),
      colors_to_avoid: splitCsv(form.colors_to_avoid, 8),
      typography_preference: form.typography_preference.trim() || null,
      competitor_references: splitLines(form.competitor_references, 8),
      packaging_format: form.packaging_format.trim() || null,
      packaging_material: form.packaging_material.trim() || null,
      claims_to_avoid: splitLines(form.claims_to_avoid, 32),
      target_language: form.target_language,
      notes: form.notes.trim() || null,
      ai_assist: aiAssist,
    };
    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("visual-direction", body, runVisualDirectionDryRun);
    setState({ kind: "loaded", result });
  }

  function onReset(): void {
    setForm({
      ...EMPTY,
      target_language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
        ? locale
        : "en") as DryRunLanguage,
    });
    setErrors({});
    setState({ kind: "idle" });
  }

  const opt = (vals: readonly string[]) =>
    vals.map((v) => ({ value: v, label: v }));

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">
          {t.appShellNavWorkflows}
        </span>
        <h1 className="po-section-header__title">{t.visualTitle}</h1>
        <p className="po-note">{t.visualSubtitle}</p>
      </header>

      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <FieldGrid>
            <TextField
              label={t.intakeFieldProductName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name}
              onChange={(v) => update("product_name", v)}
              error={errors.product_name}
              maxLength={200}
            />
            <TextField
              label={t.intakeFieldProductCategory}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.category}
              onChange={(v) => update("category", v)}
              error={errors.category}
              maxLength={100}
            />
            <TextField
              label={t.intakeFieldBrandName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.brand_name}
              onChange={(v) => update("brand_name", v)}
              error={errors.brand_name}
              maxLength={200}
            />
            <TextField
              label={t.labelCopyFieldTargetMarket}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.target_market}
              onChange={(v) => update("target_market", v.toUpperCase())}
              error={errors.target_market}
              maxLength={8}
            />
            <SelectField
              label={t.labelCopyFieldTargetLanguage}
              value={form.target_language}
              onChange={(v) =>
                update("target_language", v as DryRunLanguage)
              }
              options={opt(DRY_RUN_LANGUAGES)}
            />
            <SelectField
              label={t.labelCopyFieldPackagingObjective}
              value={form.packaging_objective}
              onChange={(v) =>
                update("packaging_objective", v as IntakeObjective)
              }
              options={opt(INTAKE_OBJECTIVES)}
            />
            <SelectField
              label={t.intakeFieldPriceBand}
              value={form.price_band}
              onChange={(v) => update("price_band", v as PriceBand)}
              options={opt(PRICE_BANDS)}
            />
            <SelectField
              label={t.labelCopyFieldSalesChannel}
              value={form.sales_channel}
              onChange={(v) => update("sales_channel", v)}
              options={opt(DRY_RUN_CHANNELS)}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <TextField
              label={t.visualFieldBrandPersonality}
              value={form.brand_personality}
              onChange={(v) => update("brand_personality", v)}
              maxLength={500}
            />
            <TextField
              label={t.visualFieldTargetCustomer}
              value={form.target_customer}
              onChange={(v) => update("target_customer", v)}
              maxLength={500}
            />
            <TextField
              label={t.visualFieldVisualPreference}
              value={form.visual_preference}
              onChange={(v) => update("visual_preference", v)}
              maxLength={500}
            />
            <TextField
              label={t.visualFieldTypographyPreference}
              value={form.typography_preference}
              onChange={(v) => update("typography_preference", v)}
              maxLength={300}
            />
            <TextField
              label={t.supplierFieldFormat}
              value={form.packaging_format}
              onChange={(v) => update("packaging_format", v)}
              maxLength={120}
            />
            <TextField
              label={t.supplierFieldMaterial}
              value={form.packaging_material}
              onChange={(v) => update("packaging_material", v)}
              maxLength={120}
            />
            <TextAreaField
              label={t.visualFieldColorsToUse}
              value={form.colors_to_use}
              onChange={(v) => update("colors_to_use", v)}
              rows={2}
              full
            />
            <TextAreaField
              label={t.visualFieldColorsToAvoid}
              value={form.colors_to_avoid}
              onChange={(v) => update("colors_to_avoid", v)}
              rows={2}
              full
            />
            <TextAreaField
              label={t.visualFieldCompetitorReferences}
              hint={t.visualFieldCompetitorReferencesHelp}
              value={form.competitor_references}
              onChange={(v) => update("competitor_references", v)}
              rows={3}
              full
            />
            <TextAreaField
              label={t.intakeFieldClaimsToAvoid}
              value={form.claims_to_avoid}
              onChange={(v) => update("claims_to_avoid", v)}
              rows={2}
              full
            />
            <TextAreaField
              label={t.intakeFieldNotes}
              value={form.notes}
              onChange={(v) => update("notes", v)}
              rows={2}
              maxLength={1000}
              full
            />
          </FieldGrid>

          {Object.keys(errors).length > 0 && (
            <p className="po-field__error">
              {t.workflowDryRunValidationFailed}
            </p>
          )}

          <AIAssistToggle
            checked={aiAssist}
            onChange={setAiAssist}
            label={t.aiAssistToggleLabel}
            hint={t.aiAssistToggleHint}
            banner={t.aiAssistDraftBanner}
            disabled={state.kind === "submitting"}
          />

          <div className="po-row">
            <Button
              variant="primary"
              type="submit"
              disabled={state.kind === "submitting"}
            >
              {state.kind === "submitting"
                ? t.visualSubmitting
                : t.visualSubmit}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={onReset}
              disabled={state.kind === "submitting"}
            >
              {t.visualReset}
            </Button>
          </div>
        </form>
      </section>

      {state.kind === "loaded" && (
        <DataState
          state={{ kind: "loaded", result: state.result }}
          t={t}
          renderOk={(data, requestId) => (
            <VisualReport data={data} requestId={requestId} t={t} />
          )}
        />
      )}
    </AppLayout>
  );
}

interface VisualReportProps {
  data: VisualDirectionResponse;
  requestId: string | null;
  t: UiStrings;
}

function VisualReport({
  data,
  requestId,
  t,
}: VisualReportProps): JSX.Element {
  return (
    <div className="po-stack" data-testid="po-visual-direction-report">
      <div className="po-halt-banner po-halt-banner--medium" role="alert">
        {t.visualSubtitle}
      </div>
      <ReportSection title={t.visualReportSummary}>
        <BulletList items={data.visual_direction_summary} />
      </ReportSection>
      <ReportSection title={t.visualReportRoutes}>
        <BulletList items={data.design_route_options} />
      </ReportSection>
      <ReportSection title={t.visualReportColor}>
        <BulletList items={data.color_direction} />
      </ReportSection>
      <ReportSection title={t.visualReportTypography}>
        <BulletList items={data.typography_direction} />
      </ReportSection>
      <ReportSection title={t.visualReportLayout}>
        <BulletList items={data.layout_direction} />
      </ReportSection>
      <ReportSection title={t.visualReportSurface}>
        <BulletList items={data.packaging_surface_priority} />
      </ReportSection>
      <ReportSection title={t.visualReportShelfEc}>
        <BulletList
          items={data.shelf_ecommerce_visual_notes}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.visualReportCultural}>
        <BulletList items={data.cultural_localization_notes} />
      </ReportSection>
      <ReportSection title={t.visualReportMockup}>
        <BulletList items={data.mockup_prompt_pack} />
      </ReportSection>
      <ReportSection title={t.visualReportHandoff}>
        <BulletList items={data.designer_handoff_notes} />
      </ReportSection>
      <ReportSection title={t.visualReportRisks}>
        <BulletList items={data.visual_risks_avoidance_notes} />
      </ReportSection>
      <ReportSection title={t.intakeReportHumanReview}>
        <BulletList items={data.human_review_required_notes} />
      </ReportSection>
      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
